/**
 * Check-In Trends Chart
 * Visualizes mood, energy, sleep, and key wellness metrics over time
 */

import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
  Legend
} from 'recharts';
import { TrendingUp, TrendingDown, Minus, Calendar, Sparkles } from 'lucide-react';
import { format, subDays, parseISO } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { TypewriterMessage } from '@/components/onboarding/TypewriterMessage';

type TimeRange = '7d' | '14d' | '30d' | '90d';

interface TrendData {
  date: string;
  displayDate: string;
  mood: number | null;
  energy: number | null;
  sleep: number | null;
  stress: number | null;
  balance: number | null;
}

const metricConfig = {
  mood: { label: 'Mood', color: 'hsl(var(--primary))' },
  energy: { label: 'Energy', color: 'hsl(var(--accent))' },
  sleep: { label: 'Sleep', color: 'hsl(220 70% 50%)' },
  stress: { label: 'Stress', color: 'hsl(0 70% 50%)' },
  balance: { label: 'Balance', color: 'hsl(280 70% 50%)' }
};

export default function CheckInTrendsChart() {
  const [timeRange, setTimeRange] = useState<TimeRange>('14d');
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>(['mood', 'energy']);

  const { data: trendData, isLoading } = useQuery({
    queryKey: ['check-in-trends', timeRange],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const days = parseInt(timeRange);
      const startDate = format(subDays(new Date(), days), 'yyyy-MM-dd');

      const { data: checkins } = await supabase
        .from('daily_checkins')
        .select('date, hours_slept, sleep_quality, body_feeling, daily_feeling, emotion')
        .eq('user_id', user.id)
        .gte('date', startDate)
        .order('date', { ascending: true });

      if (!checkins) return [];

      // Convert to numeric scores
      const feelingToScore = (feeling: string | null): number | null => {
        if (!feeling) return null;
        const map: Record<string, number> = {
          'amazing': 10, 'good': 8, 'okay': 6, 'struggling': 4, 'crisis': 2,
          'Energized & Vibrant': 10, 'Strong & Comfortable': 8, 'Neutral': 6,
          'Tired or Low Energy': 4, 'In Pain or Discomfort': 2,
          'Relaxed & At Ease': 9, 'Energised & Vital': 8, 'Tense & Tight': 3,
          'Joy & Contentment': 10, 'Peace & Calm': 8, 'Sadness & Grief': 4, 'Anxiety & Fear': 3
        };
        return map[feeling] ?? null;
      };

      return checkins.map((c): TrendData => ({
        date: c.date,
        displayDate: format(parseISO(c.date), 'MMM d'),
        mood: feelingToScore(c.daily_feeling) || feelingToScore(c.emotion),
        energy: feelingToScore(c.body_feeling),
        sleep: c.sleep_quality || (c.hours_slept ? Math.min(10, c.hours_slept * 1.25) : null),
        stress: null, // Not directly available - would need to derive
        balance: null
      }));
    }
  });

  // Calculate trends
  const trendAnalysis = useMemo(() => {
    if (!trendData || trendData.length < 3) return null;

    const halfPoint = Math.floor(trendData.length / 2);
    const firstHalf = trendData.slice(0, halfPoint);
    const secondHalf = trendData.slice(halfPoint);

    const calcAvg = (arr: TrendData[], key: keyof TrendData) => {
      const values = arr.map(d => d[key]).filter((v): v is number => v !== null);
      return values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : null;
    };

    const results: Record<string, { change: number; trend: 'up' | 'down' | 'stable' }> = {};

    ['mood', 'energy', 'sleep', 'stress'].forEach(metric => {
      const first = calcAvg(firstHalf, metric as keyof TrendData);
      const second = calcAvg(secondHalf, metric as keyof TrendData);
      
      if (first !== null && second !== null && first > 0) {
        const change = ((second - first) / first) * 100;
        results[metric] = {
          change,
          trend: change > 5 ? 'up' : change < -5 ? 'down' : 'stable'
        };
      }
    });

    return results;
  }, [trendData]);

  const toggleMetric = (metric: string) => {
    setSelectedMetrics(prev => 
      prev.includes(metric)
        ? prev.filter(m => m !== metric)
        : [...prev, metric]
    );
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;

    return (
      <div className="bg-background/95 backdrop-blur-sm border border-border rounded-lg p-3 shadow-lg">
        <p className="font-medium text-sm mb-2">{label}</p>
        {payload.map((entry: any, idx: number) => (
          <div key={idx} className="flex items-center gap-2 text-sm">
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-muted-foreground">{entry.name}:</span>
            <span className="font-medium">{entry.value?.toFixed(1) || '-'}</span>
          </div>
        ))}
      </div>
    );
  };

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="h-64 flex items-center justify-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          >
            <Sparkles className="w-6 h-6 text-primary" />
          </motion.div>
        </div>
      </Card>
    );
  }

  if (!trendData || trendData.length < 3) {
    return (
      <Card className="p-8 text-center">
        <Calendar className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
        <h3 className="font-semibold mb-2">Not Enough Data</h3>
        <p className="text-sm text-muted-foreground">
          Complete at least 3 daily check-ins to see your trends.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Typewriter */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-start gap-3"
      >
        <div className="p-2 rounded-lg bg-primary/10">
          <TrendingUp className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-foreground">Your Wellness Trends</h3>
          <div className="text-sm text-muted-foreground min-h-[2rem]">
            <TypewriterMessage
              text="Patterns emerge over time. What you see here isn't just data - it's your body and mind communicating their needs."
              speed={20}
              showCursor={false}
            />
          </div>
        </div>
      </motion.div>

      {/* Time Range Selector */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-2">
          {(['7d', '14d', '30d', '90d'] as TimeRange[]).map((range) => (
            <Button
              key={range}
              variant={timeRange === range ? 'default' : 'outline'}
              size="sm"
              onClick={() => setTimeRange(range)}
            >
              {range === '7d' ? '7 Days' : range === '14d' ? '2 Weeks' : range === '30d' ? 'Month' : '3 Months'}
            </Button>
          ))}
        </div>

        {/* Trend Badges */}
        {trendAnalysis && (
          <div className="flex gap-2 flex-wrap">
            {Object.entries(trendAnalysis).slice(0, 2).map(([metric, data]) => (
              <Badge 
                key={metric} 
                variant={data.trend === 'up' ? 'default' : data.trend === 'down' ? 'destructive' : 'secondary'}
                className="gap-1"
              >
                {data.trend === 'up' ? <TrendingUp className="w-3 h-3" /> : 
                 data.trend === 'down' ? <TrendingDown className="w-3 h-3" /> : 
                 <Minus className="w-3 h-3" />}
                {metricConfig[metric as keyof typeof metricConfig]?.label}: {data.change > 0 ? '+' : ''}{data.change.toFixed(0)}%
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* Metric Toggles */}
      <div className="flex gap-2 flex-wrap">
        {Object.entries(metricConfig).map(([key, config]) => (
          <Button
            key={key}
            variant={selectedMetrics.includes(key) ? 'default' : 'ghost'}
            size="sm"
            onClick={() => toggleMetric(key)}
            className="gap-2"
            style={{ 
              backgroundColor: selectedMetrics.includes(key) ? config.color : undefined,
              borderColor: config.color
            }}
          >
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: selectedMetrics.includes(key) ? 'white' : config.color }}
            />
            {config.label}
          </Button>
        ))}
      </div>

      {/* Chart */}
      <Card className="p-4">
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={trendData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <defs>
              {Object.entries(metricConfig).map(([key, config]) => (
                <linearGradient key={key} id={`gradient-${key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={config.color} stopOpacity={0.3}/>
                  <stop offset="95%" stopColor={config.color} stopOpacity={0}/>
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis 
              dataKey="displayDate" 
              tick={{ fontSize: 12 }} 
              className="text-muted-foreground"
            />
            <YAxis 
              domain={[0, 10]} 
              tick={{ fontSize: 12 }}
              className="text-muted-foreground"
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            
            {selectedMetrics.map(metric => (
              <React.Fragment key={metric}>
                <Area
                  type="monotone"
                  dataKey={metric}
                  name={metricConfig[metric as keyof typeof metricConfig]?.label}
                  stroke={metricConfig[metric as keyof typeof metricConfig]?.color}
                  fill={`url(#gradient-${metric})`}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  connectNulls
                />
              </React.Fragment>
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
}
